// MusicContentServices.js
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useMusicContent() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    const fetchMusicCategories = useCallback(async () => {
        try {
            const res = await API.get("/admin/inventory/music/categories");
            return res.data;
        } catch (err) {
            console.error("Failed to fetch music categories:", err);
            return [];
        }
    }, []);

    const fetchAllMusic = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/admin/inventory/music/");
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch music products.";
            setError(msg);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMusicProductById = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get(`/admin/inventory/music/${id}`);
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch product details.";
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createMusicProduct = useCallback(async (productData, onUploadProgress) => {
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            Object.keys(productData).forEach(key => {
                if (key === 'image_file' && productData[key]) {
                    formData.append('image', productData[key]);
                } else if (key === 'audio_file' && productData[key]) {
                    formData.append('audio', productData[key]);
                } else if (key === 'pdf_file' && productData[key]) {
                    formData.append('pdf', productData[key]);
                } else if (key === 'zip_file' && productData[key]) {
                    formData.append('will_upload_zip', 'true');
                } else if (key === 'track_list' && Array.isArray(productData[key])) {
                    formData.append('track_list', JSON.stringify(productData[key]));
                } else if (productData[key] !== null && productData[key] !== undefined) {
                    formData.append(key, productData[key]);
                }
            });

            const res = await API.post("/admin/inventory/music/", formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 0,  // No timeout — large ZIP uploads can take many minutes
                onUploadProgress: onUploadProgress || undefined,
            });
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Created",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create music product.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const updateMusicProduct = useCallback(async (id, productData, onUploadProgress) => {
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            Object.keys(productData).forEach(key => {
                if (key === 'image_file' && productData[key]) {
                    formData.append('image', productData[key]);
                } else if (key === 'audio_file' && productData[key]) {
                    formData.append('audio', productData[key]);
                } else if (key === 'pdf_file' && productData[key]) {
                    formData.append('pdf', productData[key]);
                } else if (key === 'zip_file' && productData[key]) {
                    formData.append('will_upload_zip', 'true');
                } else if (key === 'track_list' && Array.isArray(productData[key])) {
                    formData.append('track_list', JSON.stringify(productData[key]));
                } else if (productData[key] !== null && productData[key] !== undefined) {
                    formData.append(key, productData[key]);
                }
            });

            const res = await API.put(`/admin/inventory/music/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 0,  // No timeout — large ZIP uploads can take many minutes
                onUploadProgress: onUploadProgress || undefined,
            });
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to update music product.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const deleteMusicProduct = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.delete(`/admin/inventory/music/${id}`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Deleted",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete product.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const toggleMusicStatus = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.patch(`/admin/inventory/music/${id}/toggle-status`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to toggle status.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const downloadMusicFile = useCallback(async (id, mediaType, quality = "original") => {
        try {
            const res = await API.get(`/admin/inventory/music/download/${id}/${mediaType}`, {
                params: { quality },
                responseType: 'blob'
            });
            return res;
        } catch (err) {
            console.error("Failed to download file:", err);
            const msg = err.response?.data?.message || "Failed to download file.";
            showNotification({
                type: "failed",
                title: "Download Error",
                message: msg,
            });
            throw err;
        }
    }, [showNotification]);

    const cleanupFailedZipUpload = useCallback(async (productId) => {
        try {
            const res = await API.delete(`/admin/inventory/music/${productId}/cleanup-failed`);
            return res.data;
        } catch (err) {
            console.error("Failed to cleanup zombie metadata:", err);
            return { status: "failed" };
        }
    }, []);

    const uploadZipStream = useCallback(async (productId, zipFile, onUploadProgress) => {
        setLoading(true);
        setError(null);
        try {
            const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks
            const totalChunks = Math.ceil(zipFile.size / CHUNK_SIZE);

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, zipFile.size);
                const chunk = zipFile.slice(start, end);

                let attempts = 0;
                const maxAttempts = 3;
                let success = false;

                while (attempts < maxAttempts && !success) {
                    try {
                        await API.post(`/admin/inventory/music/${productId}/upload-zip`, chunk, {
                            headers: {
                                'Content-Type': 'application/octet-stream',
                                'X-Chunk-Index': i.toString(),
                                'X-Total-Chunks': totalChunks.toString(),
                            },
                            timeout: 0, // No timeout for large chunk uploads
                            onUploadProgress: (progressEvent) => {
                                if (onUploadProgress) {
                                    const loaded = start + progressEvent.loaded;
                                    onUploadProgress({
                                        loaded,
                                        total: zipFile.size
                                    });
                                }
                            }
                        });
                        success = true;
                    } catch (err) {
                        attempts++;
                        console.warn(`Chunk ${i} upload attempt ${attempts} failed:`, err);
                        if (attempts >= maxAttempts) throw err;
                        // Exponential backoff — capture current attempts to avoid no-loop-func
                        const currentAttempts = attempts;
                        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, currentAttempts)));
                    }
                }
            }

            return { status: "success", message: "ZIP file uploaded in chunks successfully." };
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to upload ZIP in chunks after multiple attempts.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Upload Error",
                message: msg,
            });
            // Cleanup zombie metadata
            await cleanupFailedZipUpload(productId);
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification, cleanupFailedZipUpload]);

    return {
        loading,
        error,
        fetchMusicCategories,
        fetchAllMusic,
        fetchMusicProductById,
        createMusicProduct,
        updateMusicProduct,
        deleteMusicProduct,
        toggleMusicStatus,
        downloadMusicFile,
        uploadZipStream,
        cleanupFailedZipUpload
    };
}
